import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  CircularProgress,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DownloadIcon from "@mui/icons-material/Download";
import { useDispatch, useSelector } from "react-redux";
import { fetchOTNonAllocatedLoc } from "../../action/OTNonAllocatedLocAction";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";


const BRAND = "#330066";
const EXCLUDED = ["cad", "cac", "hao", "iim", "ism", "it"];

// ─── Styled shells ────────────────────────────────────────────────────────────
const Page = styled(Box)(({ theme }) => ({
  width: "100%",
  padding: "0 0 80px",
}));

const Card = styled(Box)({
  background: "#fff",
  border: "0.5px solid #e2e8f0",
  borderRadius: 12,
  overflow: "hidden",
});

const SectionLabel = styled(Typography)({
  fontSize: 11,
  fontWeight: 600,
  color: "#94a3b8",
  textTransform: "uppercase",
  letterSpacing: "0.6px",
  marginBottom: 6,
  marginTop: 14,
});

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatDate = (d) => new Date(d).toISOString().split("T")[0];
const formatTime = (d) =>
  new Date(d).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

const getVal = (item, key) => item[key] || "";

export default function OTNonAllocated() {
  const dispatch = useDispatch();
  const { loading, data: locData } = useSelector((s) => s.otNonAllocatedLoc);

  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());

  useEffect(() => {
    dispatch(fetchOTNonAllocatedLoc(formatDate(selectedDate)));
  }, [dispatch, selectedDate]);

  // ── Filtered data ─────────────────────────────────────────────────────────
  const filteredData =
    locData?.filter((item) => {
      const loc = (item.OTNon_location || item.Location || "").toLowerCase().trim();
      return !EXCLUDED.includes(loc);
    }) || [];

  const totalCount = filteredData.reduce(
    (sum, item) => sum + Number(item.OTNon_EmpCount || 0),
    0
  );

  // ── PDF export ────────────────────────────────────────────────────────────
  const handleExportPDF = () => {
    const doc = new jsPDF("p", "mm", "a4");
    const at = new Date();
    const pw = doc.internal.pageSize.getWidth();

    doc.setFontSize(22).setFont("helvetica", "bold").setTextColor(30, 58, 110);
    doc.text("DTS Biztrack", pw / 2, 20, { align: "center" });
    doc.setFontSize(13).setTextColor(100, 116, 139);
    doc.text("Non-Allocated Employees Report", pw / 2, 30, { align: "center" });
    doc.setDrawColor(30, 58, 110).setLineWidth(0.5).line(15, 36, pw - 15, 36);

    const y = 44, sx = 15, tw = pw - 30;
    [
      [sx + tw * 0.125, "Date – ", formatDate(selectedDate)],
      [sx + tw * 0.375, "Time – ", formatTime(at)],
      [sx + tw * 0.625, "View – ", "Location"],
      [sx + tw * 0.875, "Total – ", totalCount.toString()],
    ].forEach(([col, bold, val]) => {
      const fw = doc.getTextWidth(bold + val);
      doc.setFontSize(10).setFont("helvetica", "bold").setTextColor(60, 60, 60);
      doc.text(bold, col - fw / 2, y);
      doc.setFont("helvetica", "normal").text(val, col - fw / 2 + doc.getTextWidth(bold), y);
    });

    const rows = filteredData.map((item) => [
      getVal(item, "OTNon_location") || getVal(item, "Location"),
      getVal(item, "OTNon_EmpCount") || getVal(item, "EmployeeCount"),
    ]);
    rows.push(["TOTAL", totalCount.toString()]);

    autoTable(doc, {
      head: [["Location", "Employee Count"]],
      body: rows,
      startY: 52,
      styles: { fontSize: 12, cellPadding: 5 },
      headStyles: { fillColor: [21, 76, 121], textColor: 255, fontStyle: "bold", halign: "center" },
      alternateRowStyles: { fillColor: [248, 249, 252] },
      columnStyles: {
        0: { cellWidth: 93.5, halign: "center" },
        1: { cellWidth: 93.5, halign: "center" },
      },
      theme: "grid",
      margin: { left: 13, right: 10 },
      didParseCell(data) {
        if (data.row.index === rows.length - 1) {
          data.cell.styles.fillColor = [21, 76, 121];
          data.cell.styles.textColor = 255;
          data.cell.styles.fontStyle = "bold";
        }
      },
      didDrawPage(data) {
        doc.setFontSize(8).setTextColor(150).setFont("helvetica", "italic");
        doc.text(
          `Generated on ${formatDate(at)} at ${formatTime(at)}`,
          pw / 2,
          doc.internal.pageSize.getHeight() - 10,
          { align: "center" }
        );
        doc.text(`Page ${data.pageNumber}`, pw - 20, doc.internal.pageSize.getHeight() - 10);
      },
    });

    doc.save(`Non_Allocated_Employees_${formatDate(selectedDate)}.pdf`);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Page>
        {/* ── Filter row ── */}
        <Box sx={{ display: "flex", gap: 1, mb: 1.5, alignItems: "center" }}>
          {/* Date pill */}
          <Box
            onClick={() => setOpenDatePicker(true)}
            sx={{
              height: 36, borderRadius: "20px", border: "0.5px solid #e2e8f0",
              background: "#fff", display: "flex", alignItems: "center",
              gap: "6px", px: "12px", cursor: "pointer", flexShrink: 0,
              "&:hover": { borderColor: BRAND },
            }}
          >
            <CalendarTodayIcon sx={{ fontSize: 14, color: "#64748b" }} />
            <Typography sx={{ fontSize: 12, color: "#1e293b", fontWeight: 500 }}>
              {formatDate(selectedDate)}
            </Typography>
          </Box>

          {openDatePicker && (
            <DatePicker
              open={openDatePicker}
              value={selectedDate}
              onChange={(date) => {
                setSelectedDate(date);
                setOpenDatePicker(false);
              }}
              onClose={() => setOpenDatePicker(false)}
              renderInput={(params) => <TextField {...params} sx={{ display: "none" }} />}
            />
          )}
        </Box>

        {/* ── Stats + PDF row ── */}
        {!loading && filteredData.length > 0 && (
          <Box sx={{ display: "flex", gap: 1, mb: 1.5, alignItems: "stretch" }}>
            {/* Total card */}
            <Box
              sx={{
                flex: 1, background: "#fff", border: "0.5px solid #e2e8f0",
                borderRadius: "12px", p: "10px 12px",
              }}
            >
              <Typography sx={{ fontSize: 11, color: "#64748b", mb: 0.5 }}>
                Total Employees
              </Typography>
              <Typography sx={{ fontSize: 22, fontWeight: 500, color: BRAND, lineHeight: 1.1 }}>
                {totalCount}
              </Typography>
            </Box>

            {/* PDF button */}
            <Button
              size="small"
              onClick={handleExportPDF}
              disabled={!filteredData.length}
              sx={{
                minWidth: 0, px: "14px", borderRadius: "12px",
                background: "#16a34a", color: "#fff",
                textTransform: "none", flexShrink: 0,
                display: "flex", flexDirection: "column", gap: "2px",
                "&:hover": { background: "#15803d" },
                "&:disabled": { background: "#cbd5e1", color: "#64748b" },
              }}
            >
              <DownloadIcon sx={{ fontSize: 18 }} />
              <Box component="span" sx={{ fontSize: 10, fontWeight: 500 }}>PDF</Box>
            </Button>
          </Box>
        )}

        {/* ── Table ── */}
        <SectionLabel>Location wise non-allocated</SectionLabel>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={28} sx={{ color: BRAND }} />
          </Box>
        ) : (
          <Card>
            <Table size="small" stickyHeader sx={{ tableLayout: "fixed", width: "100%" }}>
              <TableHead>
                <TableRow>
                  {["Location", "Employee Count"].map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        width: "50%", background: BRAND, color: "#fff",
                        fontSize: 11, fontWeight: 600, py: "8px", px: "10px",
                        borderBottom: "none", textAlign: "center",
                        position: "sticky", top: 0, zIndex: 10,
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredData.length > 0 ? (
                  filteredData.map((item, i) => {
                    const loc = getVal(item, "OTNon_location") || getVal(item, "Location");
                    const count = Number(getVal(item, "OTNon_EmpCount") || getVal(item, "EmployeeCount") || 0);
                    return (
                      <TableRow
                        key={i}
                        sx={{
                          "&:nth-of-type(even) td": { background: "#f8faff" },
                          "&:hover td": { background: "rgba(21,76,121,0.04)" },
                          "&:last-child td": { borderBottom: "none" },
                        }}
                      >
                        <TableCell sx={{ textAlign: "center", px: "10px", py: "8px" }}>
                          <Box
                            component="span"
                            sx={{
                              display: "inline-block", px: "8px", py: "2px",
                              borderRadius: "10px", fontSize: 11, fontWeight: 600,
                              background: "#e8f0fe", color: BRAND,
                            }}
                          >
                            {loc}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ textAlign: "center", px: "6px", py: "8px" }}>
                          <Box
                            component="span"
                            sx={{
                              display: "inline-block", px: "8px", py: "2px",
                              borderRadius: "10px", fontSize: 11, fontWeight: 600,
                              background: count > 0 ? "#fee2e2" : "#dcfce7",
                              color: count > 0 ? "#b91c1c" : "#15803d",
                            }}
                          >
                            {count}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={2}
                      sx={{ textAlign: "center", py: 6, fontSize: 13, color: "#94a3b8" }}
                    >
                      No records found
                    </TableCell>
                  </TableRow>
                )}

                {/* Total row */}
                {filteredData.length > 0 && (
                  <TableRow sx={{ "& td": { background: "#EFF4FF !important", borderBottom: "none" } }}>
                    <TableCell
                      sx={{ fontSize: 12, fontWeight: 600, color: BRAND, px: "10px", py: "8px", textAlign: "center" }}
                    >
                      Total
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", px: "6px", py: "8px" }}>
                      <Box
                        component="span"
                        sx={{
                          display: "inline-block", px: "8px", py: "2px",
                          borderRadius: "10px", fontSize: 11, fontWeight: 600,
                          background: "#fff", color: BRAND, border: "0.5px solid #c7d7f8",
                        }}
                      >
                        {totalCount}
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        )}
      </Page>
    </LocalizationProvider>
  );
}